import { z } from 'zod';

// Office Range Schema
export const OfficeRangeSchema = z.object({
  start: z.number().int().positive('Start office number must be positive'),
  end: z.number().int().positive('End office number must be positive')
}).refine((data) => data.end >= data.start, {
  message: 'End office number must be greater than or equal to start',
  path: ['end']
});

export type OfficeRange = z.infer<typeof OfficeRangeSchema>;

// Base Region Schema
export const BaseRegionSchema = z.object({
  regionId: z.number().int().positive('Region ID must be positive'),
  name: z.string().min(1, 'Region name is required').max(100, 'Region name too long'),
  controllerId: z.string().optional(), // Reference to Controller
  allowedNoOfOffices: z.number().int().positive('Allowed number of offices must be positive')
    .max(100, 'Maximum 100 offices allowed per region'),
  officeRange: OfficeRangeSchema.optional(),
  isActive: z.boolean().default(true),
  createdAt: z.date().optional(),
  updatedAt: z.date().optional()
});

export type BaseRegion = z.infer<typeof BaseRegionSchema>;

// Region Creation Schema
export const CreateRegionSchema = BaseRegionSchema.omit({
  regionId: true,
  officeRange: true,
  createdAt: true,
  updatedAt: true
});

export type CreateRegion = z.infer<typeof CreateRegionSchema>;

// Region Update Schema
export const UpdateRegionSchema = BaseRegionSchema.partial().extend({
  regionId: z.number() // Required for updates
});

export type UpdateRegion = z.infer<typeof UpdateRegionSchema>;

// Region Response Schema (for API responses)
export const RegionResponseSchema = BaseRegionSchema.extend({
  regionId: z.number(),
  officeRange: OfficeRangeSchema.optional()
});

export type RegionResponse = z.infer<typeof RegionResponseSchema>;

// Region with Office Count Schema
export const RegionWithOfficeCountSchema = RegionResponseSchema.extend({
  currentOfficeCount: z.number().int().min(0),
  availableOfficeSlots: z.number().int().min(0)
});

export type RegionWithOfficeCount = z.infer<typeof RegionWithOfficeCountSchema>;

// Region List Query Schema
export const RegionListQuerySchema = z.object({
  page: z.number().int().min(1).default(1),
  limit: z.number().int().min(1).max(100).default(20),
  search: z.string().optional(),
  isActive: z.boolean().optional(),
  sortBy: z.enum(['regionId', 'name', 'createdAt']).default('regionId'),
  sortOrder: z.enum(['asc', 'desc']).default('asc')
});

export type RegionListQuery = z.infer<typeof RegionListQuerySchema>;

// Region Statistics Schema
export const RegionStatisticsSchema = z.object({
  regionId: z.number(),
  regionName: z.string(),
  totalOffices: z.number(),
  totalDonations: z.number(),
  totalAmount: z.number(),
  period: z.object({
    start: z.date(),
    end: z.date()
  })
});

export type RegionStatistics = z.infer<typeof RegionStatisticsSchema>; 